import { useState } from 'react';
import { toSocialUrl, displayUrl } from '../lib/socialUrl';
import { Instagram, Youtube, Music2, Facebook, Linkedin, Globe, X as XIcon, Twitch, Plus, Check, Trash2 } from 'lucide-react';

/**
 * SocialLinksDots — compact row of circular social buttons.
 *
 * Renders 8 small dots (Instagram · YouTube · X · TikTok · Facebook ·
 * LinkedIn · Twitch · Website). Empty dots show a tiny "+" badge;
 * filled dots light up in brand color with a green check. Tapping any
 * dot expands an inline URL input below the row so users can add or
 * edit without leaving the builder.
 *
 * `value` shape — matches the athlete/agent data model:
 *   { instagram: string, social_links: { tiktok, youtube, twitter, facebook, linkedin, twitch, website } }
 * `onChange` fires with the same shape whenever a URL is saved or cleared.
 */

// iter816 · Jay: type just the handle (like LinkMe) and we build the
// right platform URL automatically. iter878 · The builder now lives in
// `lib/socialUrl` (official URL format + username rules per platform)
// and the full link previews live as the athlete types.
const PLATFORMS = [
  { key: 'instagram', label: 'Instagram', icon: Instagram, color: '#E4405F', field: 'top' },
  { key: 'youtube',   label: 'YouTube',   icon: Youtube,   color: '#FF0000', field: 'social' },
  { key: 'twitter',   label: 'X',         icon: XIcon,     color: '#FFFFFF', field: 'social' },
  { key: 'tiktok',    label: 'TikTok',    icon: Music2,    color: '#25F4EE', field: 'social' },
  { key: 'facebook',  label: 'Facebook',  icon: Facebook,  color: '#1877F2', field: 'social' },
  { key: 'linkedin',  label: 'LinkedIn',  icon: Linkedin,  color: '#0A66C2', field: 'social' },
  // iter374 · Twitch added — a growing share of athletes stream on it.
  { key: 'twitch',    label: 'Twitch',    icon: Twitch,    color: '#9146FF', field: 'social' },
  { key: 'website',   label: 'Website',   icon: Globe,     color: '#8B5CF6', field: 'social' },
];
const formatInput = (p, raw) => toSocialUrl(p.key, raw);

const readValue = (value, p, flat) => {
  if (!value) return '';
  if (flat) return value[p.key] || '';
  return p.field === 'top' ? (value[p.key] || '') : (value.social_links?.[p.key] || '');
};

const writeValue = (value, p, next, flat) => {
  const clean = (next || '').trim();
  // iter454.9 · When clearing a social link (empty string), DELETE the
  // key entirely from the object rather than storing an empty string.
  // Prevents stale keys lingering in state that could re-surface after
  // a partial save, or trip up backend merges that skip empty values.
  if (!clean) {
    if (flat) {
      const next = { ...value };
      delete next[p.key];
      return next;
    }
    if (p.field === 'top') {
      const nextV = { ...value };
      delete nextV[p.key];
      return nextV;
    }
    const nextSocial = { ...(value?.social_links || {}) };
    delete nextSocial[p.key];
    return { ...value, social_links: nextSocial };
  }
  if (flat) return { ...value, [p.key]: clean };
  if (p.field === 'top') return { ...value, [p.key]: clean };
  return { ...value, social_links: { ...(value?.social_links || {}), [p.key]: clean } };
};

const SocialLinksDots = ({ value, onChange, label = 'Social Links', flat = false }) => {
  const [openKey, setOpenKey] = useState(null);
  const [draft, setDraft] = useState('');

  const openEditor = (p) => {
    setOpenKey(p.key);
    setDraft(readValue(value, p, flat));
  };

  const save = () => {
    const p = PLATFORMS.find((x) => x.key === openKey);
    if (!p) return;
    onChange(writeValue(value, p, formatInput(p, draft), flat));
    setOpenKey(null);
  };

  const clear = () => {
    const p = PLATFORMS.find((x) => x.key === openKey);
    if (!p) return;
    onChange(writeValue(value, p, '', flat));
    setOpenKey(null);
  };

  const active = openKey ? PLATFORMS.find((p) => p.key === openKey) : null;
  const filledCount = PLATFORMS.filter((p) => readValue(value, p, flat).trim()).length;

  return (
    <div className="rounded-2xl ring-1 ring-white/10 bg-white/[0.02] p-4" data-testid="social-links-dots">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-white/60">{label}</p>
        <p className="text-[10px] uppercase tracking-widest text-white/40 font-bold">
          {filledCount} of {PLATFORMS.length} linked
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
        {PLATFORMS.map((p) => {
          const val = readValue(value, p, flat);
          const filled = !!val.trim();
          const Icon = p.icon;
          const isOpen = openKey === p.key;
          return (
            <button
              type="button"
              key={p.key}
              onClick={() => openEditor(p)}
              data-testid={`social-dot-${p.key}`}
              title={p.label + (filled ? ' · ' + val : '')}
              className={`relative w-12 h-12 rounded-full flex items-center justify-center ring-1 transition ${
                filled
                  ? 'bg-white/[0.05] ring-white/25 hover:bg-white/[0.1]'
                  : 'bg-black/40 ring-white/10 hover:ring-white/25'
              } ${isOpen ? 'ring-blue-400 ring-2' : ''}`}
            >
              <Icon
                className="w-5 h-5"
                strokeWidth={2}
                style={{ color: filled ? p.color : 'rgba(255,255,255,0.55)' }}
              />
              {filled ? (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 ring-2 ring-black flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                </span>
              ) : (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-blue-500 ring-2 ring-black flex items-center justify-center">
                  <Plus className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {active && (
        <div className="mt-4 rounded-xl ring-1 ring-white/10 bg-black/40 p-3">
          <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-blue-300 mb-2 inline-flex items-center gap-1.5">
            <active.icon className="w-3.5 h-3.5" strokeWidth={2} />
            {active.label}
          </p>
          <input
            type="text"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={active.key === 'website' ? 'yoursite.com' : 'yourhandle'}
            data-testid={`social-input-${active.key}`}
            /* iter362 · WKWebView on iOS was forcing the input to a
               solid white background (looks like autofill-eligible
               URL fields trigger it), making the previous white text
               invisible. Pin both background AND text color via
               inline style with !important-equivalent priority so
               iOS can't override either. Matches the pattern used
               in AthleteBasicsEditor. */
            className="w-full rounded-lg ring-1 ring-white/10 focus:ring-blue-400 outline-none px-3 py-2.5 text-[16px] placeholder:text-white/30"
            style={{
              color: '#ffffff',
              caretColor: '#ffffff',
              backgroundColor: 'rgba(0,0,0,0.55)',
              WebkitTextFillColor: '#ffffff',
            }}
          />
          {/* iter878 · Live preview — the full link fills in as they type. */}
          {formatInput(active, draft) && (
            <p className="mt-2 text-[12px] text-emerald-300 inline-flex items-center gap-1.5 break-all" data-testid={`social-preview-${active.key}`}>
              <Check className="w-3.5 h-3.5 shrink-0" strokeWidth={3} />
              {displayUrl(formatInput(active, draft))}
            </p>
          )}
          <div className="mt-2 flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={() => setOpenKey(null)}
              data-testid="social-cancel"
              className="px-3 py-1.5 rounded-full ring-1 ring-white/15 text-white/70 text-[10px] uppercase tracking-widest font-bold hover:bg-white/[0.05]"
            >
              Cancel
            </button>
            {readValue(value, active, flat).trim() && (
              <button
                type="button"
                onClick={clear}
                data-testid="social-clear"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full ring-1 ring-red-400/40 text-red-300 text-[10px] uppercase tracking-widest font-bold hover:bg-red-500/10"
              >
                <Trash2 className="w-3 h-3" strokeWidth={2} />
                Remove
              </button>
            )}
            <button
              type="button"
              onClick={save}
              data-testid="social-save"
              className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-white text-black text-[10px] font-black uppercase tracking-widest hover:bg-blue-200"
            >
              <Check className="w-3 h-3" strokeWidth={3} />
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialLinksDots;
